import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Bell, BellOff, Volume2, VolumeX, Moon, Send, Zap, Sparkles, Webhook,
  Users, Crown, TrendingUp, Clock, AlertTriangle, Trash2, PlayCircle, Check,
  Radio, ShieldCheck, Slack,
} from "lucide-react";
import {
  getNotifPrefs, setNotifPrefs, playSound, flashFavicon, browserNotify,
  requestDesktopPermission, fireAlert, getNotifLog, clearLog,
  SOUND_STYLES, EVENTS, CHANNELS,
} from "@/lib/adminNotify";

const AUDIENCES = [
  { v: "all_customers", label: "All customers", icon: Users, tone: "slate" },
  { v: "vip", label: "VIP ($500+ lifetime)", icon: Crown, tone: "amber" },
  { v: "active_30d", label: "Active last 30 days", icon: TrendingUp, tone: "emerald" },
  { v: "pending_payouts", label: "Pending payouts", icon: Clock, tone: "sky" },
  { v: "no_submission", label: "Never submitted", icon: AlertTriangle, tone: "fuchsia" },
];

const BROADCAST_TEMPLATES = [
  { key: "rate_boost", title: "🔥 Rate boost this weekend", body: "For 48 hours, Amazon + Target payouts jump to 92%. Submit yours before Monday to lock it in." },
  { key: "maintenance", title: "Brief maintenance tonight", body: "We'll be doing a 15-minute maintenance window at 2 AM ET. Payouts and submissions will queue and process right after." },
  { key: "referral_boost", title: "Refer a friend, both get $10", body: "Share your referral code — we'll credit $10 to your friend and $10 to you the moment they get their first payout." },
  { key: "payout_delay", title: "Payment provider delay notice", body: "Cash App is running slow on their side today. Your payout is confirmed and en route — expect it within 2 hours." },
  { key: "new_brand", title: "New card brand accepted", body: "We now accept Sephora and Ulta gift cards at 82% for physical, 76% for digital. Submit yours anytime." },
];

const CHANNEL_ICONS = {
  sound: Volume2, desktop: Sparkles, favicon: Zap, push: Bell, webhook: Slack,
};

const TABS = [
  { v: "alerts", label: "My alerts", icon: Bell },
  { v: "broadcast", label: "Broadcast", icon: Radio },
  { v: "history", label: "History", icon: ShieldCheck },
];

export default function AdminNotifications() {
  const [tab, setTab] = useState("alerts");
  const [prefs, setPrefs] = useState(getNotifPrefs);
  const [log, setLog] = useState(getNotifLog);
  const [broadcasts, setBroadcasts] = useState([]);
  const [segments, setSegments] = useState({});
  const [form, setForm] = useState({ title: "", body: "", audience: "all_customers", channel: "in_app" });
  const [sending, setSending] = useState(false);
  const [desktopPerm, setDesktopPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  const update = useCallback((patch) => {
    setPrefs((p) => {
      const next = { ...p, ...patch, matrix: { ...p.matrix, ...(patch.matrix || {}) } };
      setNotifPrefs(next);
      return next;
    });
  }, []);
  const updateMatrix = (event, channel, on) => {
    setPrefs((p) => {
      const next = {
        ...p,
        matrix: { ...p.matrix, [event]: { ...p.matrix[event], [channel]: on } },
      };
      setNotifPrefs(next);
      return next;
    });
  };
  const toggleQuietDay = (day) => {
    const days = new Set(prefs.quiet_days || []);
    days.has(day) ? days.delete(day) : days.add(day);
    update({ quiet_days: Array.from(days) });
  };

  const loadBroadcasts = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/notifications");
      setBroadcasts((r.data || []).filter((n) => !n.target_customer_id).slice(0, 30));
    } catch { /* */ }
  }, []);
  const loadSegments = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/notifications/segments");
      setSegments(r.data || {});
    } catch { /* */ }
  }, []);
  useEffect(() => { loadBroadcasts(); loadSegments(); }, [loadBroadcasts, loadSegments]);
  useEffect(() => {
    if (tab === "history") setLog(getNotifLog());
  }, [tab]);

  const testAll = () => {
    const r = fireAlert("new_chat", "Test alert", "This is what a manager-requested chat will feel like.");
    setLog(getNotifLog());
    if (r.fired) toast.success(`Fired via: ${r.channels.join(", ")}`);
    else toast.error(`Blocked: ${r.reason.replace("_", " ")}`);
  };
  const previewSound = () => playSound(prefs.sound_style, prefs.volume);
  const previewFavicon = () => flashFavicon();
  const previewDesktop = async () => {
    const p = await requestDesktopPermission();
    setDesktopPerm(p);
    if (p !== "granted") return toast.error("Desktop permission not granted");
    browserNotify("Miles handed off a chat", "A customer just asked to speak with a human. Take a look.");
    toast.success("Desktop popup fired (if your tab is in the background)");
  };
  const testWebhook = async () => {
    if (!prefs.webhook_url) return toast.error("Enter a webhook URL first");
    try {
      await fetch(prefs.webhook_url, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ text: "*GC Nexon test alert*\nIf you're seeing this in Slack/Discord, the webhook is wired correctly." }),
      });
      toast.success("Sent to webhook");
    } catch { toast.error("Webhook failed"); }
  };

  const sendBroadcast = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and message required");
    setSending(true);
    try {
      const r = await adminApi.post("/admin/notifications/broadcast", form);
      toast.success(`Announcement sent to ${r.data.reach} customer${r.data.reach === 1 ? "" : "s"}`);
      setForm({ title: "", body: "", audience: form.audience, channel: form.channel });
      loadBroadcasts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    finally { setSending(false); }
  };
  const applyTemplate = (t) => setForm({ ...form, title: t.title, body: t.body });

  const clearHistory = () => { clearLog(); setLog([]); toast.success("Log cleared"); };

  const activeBroadcasts = broadcasts.filter((b) => b.broadcast_reach !== undefined);

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-notifications">
        {/* Title */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">Notifications</span>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1" data-testid="admin-notifications-title">Alerts &amp; broadcasts</h1>
            <p className="text-slate-500 mt-1 text-sm">Tune your own alerts and push announcements to customer segments.</p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="mt-6 flex gap-1 border-b border-slate-200">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.v} onClick={() => setTab(t.v)} data-testid={`notif-tab-${t.v}`}
                className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-black transition ${tab === t.v ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                <Icon size={14} /> {t.label}
                {tab === t.v && <span className="absolute left-2 right-2 -bottom-px h-[3px] rounded-t-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />}
              </button>
            );
          })}
        </div>

        {/* ALERTS TAB */}
        {tab === "alerts" && (
          <div className="mt-6 grid lg:grid-cols-3 gap-4">

            <div className="lg:col-span-2 space-y-4">
              {/* Master + preview */}
              <Card>
                <div className="flex items-start gap-3">
                  <IconBox tone="rose"><Bell size={16} /></IconBox>
                  <div className="flex-1">
                    <div className="text-sm font-black text-slate-900">Notifications</div>
                    <div className="text-xs text-slate-500 mt-0.5">Master switch — flipping this off silences every channel below.</div>
                  </div>
                  <Toggle checked={prefs.enabled} onChange={(v) => update({ enabled: v })} testId="notif-master" />
                </div>
                {!prefs.enabled && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2">
                    <BellOff size={13} /> All alerts are OFF. Nothing will ping until you switch back on.
                  </div>
                )}
                {prefs.enabled && (
                  <button onClick={testAll} data-testid="notif-test-all"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white text-xs font-black">
                    <PlayCircle size={14} /> Preview all alerts
                  </button>
                )}
              </Card>

              {/* Channels */}
              <Card title="Channels" hidden={!prefs.enabled}>
                {/* Sound */}
                <div className="py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <IconBox tone="sky"><Volume2 size={14} /></IconBox>
                    <div className="flex-1">
                      <div className="text-sm font-black text-slate-900">Sound alerts</div>
                      <div className="text-xs text-slate-500">A short chime plays in the admin tab.</div>
                    </div>
                    <button onClick={previewSound} data-testid="notif-sound-preview" className="text-xs font-bold text-sky-600 hover:text-sky-800">Preview</button>
                  </div>
                  <div className="mt-3 pl-11 grid sm:grid-cols-[1fr,auto] gap-3">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Sound style</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {SOUND_STYLES.map((s) => (
                          <button key={s} onClick={() => { update({ sound_style: s }); playSound(s, prefs.volume); }} data-testid={`notif-sound-${s}`}
                            className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-black transition ${
                              prefs.sound_style === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                            }`}>
                            {prefs.sound_style === s && <Check size={12} />} {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Volume</div>
                      <input type="range" min={0} max={1} step={0.05} value={prefs.volume} onChange={(e) => update({ volume: Number(e.target.value) })} data-testid="notif-volume"
                        className="w-40 mt-3 accent-indigo-500" />
                    </div>
                  </div>
                </div>

                {/* Desktop popup */}
                <div className="py-3 border-b border-slate-100 last:border-0 flex items-center gap-3">
                  <IconBox tone="amber"><Sparkles size={14} /></IconBox>
                  <div className="flex-1">
                    <div className="text-sm font-black text-slate-900">Desktop popup</div>
                    <div className="text-xs text-slate-500">Browser notification when your tab is in the background. Permission: <b className={desktopPerm === "granted" ? "text-emerald-600" : "text-rose-600"}>{desktopPerm}</b></div>
                  </div>
                  <button onClick={previewDesktop} data-testid="notif-desktop-preview" className="px-3 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">Preview</button>
                </div>

                {/* Favicon */}
                <div className="py-3 border-b border-slate-100 last:border-0 flex items-center gap-3">
                  <IconBox tone="fuchsia"><Zap size={14} /></IconBox>
                  <div className="flex-1">
                    <div className="text-sm font-black text-slate-900">Favicon flash</div>
                    <div className="text-xs text-slate-500">Red badge on the tab icon for 10 seconds — visible from other tabs.</div>
                  </div>
                  <button onClick={previewFavicon} data-testid="notif-favicon-preview" className="px-3 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">Preview</button>
                </div>

                {/* Webhook */}
                <div className="py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <IconBox tone="emerald"><Webhook size={14} /></IconBox>
                    <div className="flex-1">
                      <div className="text-sm font-black text-slate-900">Slack / Discord webhook</div>
                      <div className="text-xs text-slate-500">Alerts get POST&apos;ed as JSON to your webhook URL.</div>
                    </div>
                    <button onClick={testWebhook} data-testid="notif-webhook-test" disabled={!prefs.webhook_url} className="px-3 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700 disabled:opacity-40">Test</button>
                  </div>
                  <div className="mt-2 pl-11">
                    <input value={prefs.webhook_url} onChange={(e) => update({ webhook_url: e.target.value })} placeholder="https://hooks.slack.com/services/..." data-testid="notif-webhook-url"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-xs font-mono focus:outline-none focus:border-indigo-400" />
                  </div>
                </div>
              </Card>

              {/* Event × Channel matrix */}
              <Card title="Event routing" hidden={!prefs.enabled}>
                <div className="overflow-x-auto -mx-5">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="text-left pl-5 py-3 font-black">Event</th>
                        {CHANNELS.map((c) => (
                          <th key={c.key} className="text-center px-1 py-3 font-black">
                            <div className="flex flex-col items-center gap-1">
                              {(() => { const I = CHANNEL_ICONS[c.key]; return <I size={13} className="text-slate-600" />; })()}
                              <span className="text-[9px]">{c.label}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {EVENTS.map((e) => (
                        <tr key={e.key} className="border-b border-slate-50 last:border-0">
                          <td className="pl-5 py-3">
                            <div className="text-sm font-black text-slate-800">{e.label}</div>
                            <div className="text-xs text-slate-500">{e.desc}</div>
                          </td>
                          {CHANNELS.map((c) => {
                            const on = prefs.matrix?.[e.key]?.[c.key];
                            return (
                              <td key={c.key} className="text-center px-1 py-3">
                                <button onClick={() => updateMatrix(e.key, c.key, !on)} data-testid={`notif-matrix-${e.key}-${c.key}`}
                                  className={`w-6 h-6 rounded-md flex items-center justify-center transition ${
                                    on ? "bg-indigo-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-400"
                                  }`}>
                                  {on && <Check size={13} />}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            {/* Right column: quiet hours + rate limit */}
            <div className="space-y-4">
              <Card title="Quiet hours" hidden={!prefs.enabled}>
                <div className="flex items-start gap-3">
                  <IconBox tone="indigo"><Moon size={14} /></IconBox>
                  <div className="text-xs text-slate-500 flex-1">No sound, popup or webhook during this window. Phone push obeys the phone&apos;s own Do Not Disturb.</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">From</div>
                    <input type="time" value={prefs.quiet_start || ""} onChange={(e) => update({ quiet_start: e.target.value })} data-testid="notif-quiet-start"
                      className="mt-1.5 w-full h-10 px-2 rounded-lg border border-slate-200 text-sm" />
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">To</div>
                    <input type="time" value={prefs.quiet_end || ""} onChange={(e) => update({ quiet_end: e.target.value })} data-testid="notif-quiet-end"
                      className="mt-1.5 w-full h-10 px-2 rounded-lg border border-slate-200 text-sm" />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Silent days</div>
                  <div className="flex gap-1">
                    {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => {
                      const on = (prefs.quiet_days || []).includes(i);
                      return (
                        <button key={i} onClick={() => toggleQuietDay(i)} data-testid={`notif-day-${i}`}
                          className={`w-9 h-9 rounded-lg text-xs font-black transition ${
                            on ? "bg-indigo-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                          }`}>{d}</button>
                      );
                    })}
                  </div>
                </div>
              </Card>

              <Card title="Rate limit" hidden={!prefs.enabled}>
                <div className="flex items-start gap-3">
                  <IconBox tone="rose"><AlertTriangle size={14} /></IconBox>
                  <div className="flex-1">
                    <div className="text-sm font-black text-slate-900">Max alerts / hour</div>
                    <div className="text-xs text-slate-500">Prevents alert flood during traffic spikes.</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <input type="range" min={0} max={120} step={10} value={prefs.rate_limit_per_hour} onChange={(e) => update({ rate_limit_per_hour: Number(e.target.value) })} data-testid="notif-rate-limit"
                    className="flex-1 accent-rose-500" />
                  <div className="w-24 text-right">
                    <span className="text-2xl font-black text-rose-600 tabular-nums">{prefs.rate_limit_per_hour || "∞"}</span>
                    <div className="text-[10px] text-slate-400 font-black uppercase">per hour</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* BROADCAST TAB */}
        {tab === "broadcast" && (
          <div className="mt-6 grid lg:grid-cols-[1fr,360px] gap-4">
            <Card>
              <h3 className="font-black text-slate-900 mb-4">Compose announcement</h3>

              {/* Templates */}
              <div className="mb-5">
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Templates</div>
                <div className="flex flex-wrap gap-2">
                  {BROADCAST_TEMPLATES.map((t) => (
                    <button key={t.key} onClick={() => applyTemplate(t)} data-testid={`broadcast-tpl-${t.key}`}
                      className="text-left px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-800 text-xs font-black border border-indigo-200 transition max-w-[220px] truncate">
                      {t.title}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Title</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={80} placeholder="Short, punchy — under 80 chars" data-testid="broadcast-title"
                  className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-indigo-400" />
                <div className="text-[10px] text-slate-400 mt-1 text-right tabular-nums">{form.title.length}/80</div>
              </div>

              <div className="mt-3">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Message</label>
                <textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={280} placeholder="Keep it under 280 characters. Give a clear next step." data-testid="broadcast-body"
                  className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:border-indigo-400" />
                <div className="text-[10px] text-slate-400 mt-1 text-right tabular-nums">{form.body.length}/280</div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Audience</label>
                  <div className="mt-1.5 space-y-1.5">
                    {AUDIENCES.map((a) => {
                      const Icon = a.icon;
                      const reach = segments[a.v] || 0;
                      return (
                        <button key={a.v} onClick={() => setForm({ ...form, audience: a.v })} data-testid={`broadcast-audience-${a.v}`}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-black transition ${
                            form.audience === a.v ? "bg-slate-900 text-white" : "bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200"
                          }`}>
                          <span className="inline-flex items-center gap-1.5"><Icon size={12} /> {a.label}</span>
                          <span className={`text-[10px] tabular-nums ${form.audience === a.v ? "text-white/80" : "text-indigo-600"}`}>{reach}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Channel</label>
                  <div className="mt-1.5 space-y-1.5">
                    {[
                      { v: "in_app", label: "In-app inbox", desc: "Shows in customer's dashboard bell" },
                      { v: "email", label: "Email", desc: "Sends via Resend (needs template)" },
                      { v: "push", label: "Web push", desc: "Native browser popup" },
                    ].map((c) => (
                      <button key={c.v} onClick={() => setForm({ ...form, channel: c.v })} data-testid={`broadcast-channel-${c.v}`}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition ${
                          form.channel === c.v ? "bg-indigo-50 border-2 border-indigo-400 text-indigo-900" : "bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700"
                        }`}>
                        <div className="font-black">{c.label}</div>
                        <div className="text-[10px] opacity-80 mt-0.5">{c.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Reach: <span className="text-indigo-600 font-black tabular-nums">{segments[form.audience] || 0}</span> customer{(segments[form.audience] || 0) === 1 ? "" : "s"}
                </div>
                <button onClick={sendBroadcast} disabled={sending || !form.title.trim() || !form.body.trim()} data-testid="broadcast-send"
                  className="inline-flex items-center gap-2 px-6 h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white text-sm font-black disabled:opacity-50">
                  <Send size={14} /> {sending ? "Sending…" : "Send announcement"}
                </button>
              </div>
            </Card>

            {/* Live preview */}
            <div className="space-y-4">
              <Card title="Live preview">
                <div className="rounded-xl bg-slate-900 text-white p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 mb-2">
                    <Bell size={11} /> GC Nexon
                  </div>
                  <div className="text-sm font-black">{form.title || "Announcement title"}</div>
                  <div className="text-xs text-slate-300 mt-1.5 whitespace-pre-line">{form.body || "This is how your message will appear in the customer's inbox."}</div>
                </div>
              </Card>
              <Card title="Recent broadcasts">
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {activeBroadcasts.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No broadcasts yet</div>}
                  {activeBroadcasts.map((b) => (
                    <div key={b.id} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="text-sm font-black text-slate-900">{b.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{b.body}</div>
                      <div className="flex items-center justify-between mt-2 text-[10px]">
                        <span className="font-black uppercase tracking-wider text-indigo-600">{(b.audience || "").replace(/_/g, " ")}</span>
                        <span className="text-slate-400">reach {b.broadcast_reach ?? 0} · {new Date(b.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === "history" && (
          <div className="mt-6">
            <Card>
              <div className="flex items-center justify-between">
                <h3 className="font-black text-slate-900">Alert history (this browser)</h3>
                <button onClick={clearHistory} disabled={!log.length} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-40">
                  <Trash2 size={13} /> Clear log
                </button>
              </div>
              <div className="mt-4 divide-y divide-slate-100">
                {log.length === 0 && <div className="py-12 text-center text-sm text-slate-400">No alerts fired yet — try &ldquo;Preview all&rdquo; on the Alerts tab.</div>}
                {log.map((l, i) => (
                  <div key={i} className="py-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-black text-slate-900">{l.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{l.body}</div>
                      <div className="text-[10px] text-slate-400 font-black uppercase mt-1 tracking-wider">{l.event.replace(/_/g, " ")}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-[10px] text-slate-400">{new Date(l.at).toLocaleTimeString()}</div>
                      <div className="mt-1 flex justify-end gap-1">
                        {l.channels.map((c) => {
                          const Icon = CHANNEL_ICONS[c];
                          return (
                            <span key={c} className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-indigo-100 text-indigo-700" title={c}>
                              <Icon size={11} />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Card({ title, children, hidden }) {
  if (hidden) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      {title && <div className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-3">{title}</div>}
      {children}
    </div>
  );
}
function IconBox({ children, tone = "slate" }) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    rose: "bg-rose-100 text-rose-600",
    sky: "bg-sky-100 text-sky-600",
    amber: "bg-amber-100 text-amber-600",
    fuchsia: "bg-indigo-100 text-indigo-600",
    emerald: "bg-emerald-100 text-emerald-600",
    indigo: "bg-indigo-100 text-indigo-600",
  };
  return <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${map[tone]}`}>{children}</div>;
}
function Toggle({ checked, onChange, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}
